import { formatInternationalPhoneNumber } from "@brexhq/component-library";
import { useMachine } from "@xstate/react";
import { useAtomValue } from "jotai";
import Cookies from "js-cookie";
import * as React from "react";
import { v4 as uuidv4 } from "uuid";

import {
  isTechEventUrl,
  isOldEECountEnumType,
  isOldSalesVolume,
  isOldCompanyType,
} from "@brexhq/next/src/components/modules/LeadForm/helpers/helpers";
import { externalEventMachine } from "@brexhq/next/src/components/modules/LeadForm/machines/externalEventMachine";
import { gtmMachine } from "@brexhq/next/src/components/modules/LeadForm/machines/gtmMachine";
import { obs4Machine } from "@brexhq/next/src/components/modules/LeadForm/machines/obs4Machine";
import {
  LeadFormSteps,
  UpdateLeadResponse,
  leadFormMachineContext,
} from "@brexhq/next/src/components/modules/LeadForm/machines/types";
import {
  FormType,
  HiddenFields,
} from "@brexhq/next/src/components/modules/LeadForm/sharedTypes";
import {
  useCreateLead,
  useUpdateLead,
  useGetLead,
} from "@brexhq/next/src/components/modules/LeadForm/utils";
import useAnalytics from "@brexhq/next/src/hooks/useAnalytics";
import useLocation from "@brexhq/next/src/hooks/useLocation";
import {
  FormValues,
  PartnerRefValues,
} from "@brexhq/next/src/routes/signup/Form";
import { useSoftSignup } from "@brexhq/next/src/routes/signup/hooks/useSoftSignup";
import { referrerAtom } from "@brexhq/next/src/state/prevPath";
import {
  LeadCreatedEvent,
  LeadUpdatedEvent,
} from "@brexhq/next/src/telemetry/analyticsEvents";
import {
  getApplicableRefCodes,
  getEdition,
  getFBC,
  getFBP,
  getGCLID,
  getLiFatId,
  getRefCode,
  getUserRefCode,
} from "@brexhq/next/src/telemetry/attributionMetaData";
import {
  anonymousIdStorageKey,
  getAnonymousId,
  getOrCreateAnonymousId,
} from "@brexhq/next/src/telemetry/initAnalytics";
import { getCookieCategories } from "@brexhq/next/src/utils/analytics";
import updateCookie from "@brexhq/next/src/utils/updateCookies";
import {
  type CreateLeadInput,
  type UpdateLeadInput,
  LeadFormStatus,
  LeadFormType,
  EmployeeCountV3,
  LeadDetailsInput,
  CompanyTypeV3,
  FundingSourceV3,
  SalesVolumeV3,
  EmpowerTier,
} from "@brexhq/next/types/brex-api";

import { gtmEmailOnlyMachine } from "../machines/gtmEmailMachine";

import { useSignupV2 } from "./useSignupV2";

export const useLeadformMachine = (
  programName: string,
  hiddenFields?: HiddenFields,
  formType?: string,
  partnerRef?: PartnerRefValues | null,
  isInlineEmail?: boolean,
) => {
  const { pathname } = useLocation();
  const { identify, trackEvent, trackEarlyAccountSignupSuccess } =
    useAnalytics();
  const prevPath = useAtomValue(referrerAtom);
  const { submitUserDataWithFormValue } = useSoftSignup({
    partnerRef,
    formValues: {} as FormValues,
    trackEarlyAccountSignupSuccess,
    anonymousId: getOrCreateAnonymousId(anonymousIdStorageKey),
  });
  const { submitUserData } = useSignupV2({
    partnerRef,
    trackEarlyAccountSignupSuccess,
    anonymousId: getOrCreateAnonymousId(anonymousIdStorageKey),
  });
  const createLead = useCreateLead();
  const updateLead = useUpdateLead();
  const getLead = useGetLead();

  /**
   * All typecasts except for employee count have the same value, but different enums.
   * Normally type casting like this is dangerous since we're forcing types here,
   * but since we're submitting both versions during the rollout it's an interim solution
   * TODO: deprecate the OBS3 logic
   */
  const getLeadDetails = React.useCallback(
    (context: leadFormMachineContext): LeadDetailsInput => {
      const {
        firstName,
        lastName,
        employeeCount,
        companyName,
        mobileNumber,
        workEmail,
        isEcomm,
        country,
        monthlySales,
        refCode,
        salesRepName,
        website,
        linkedinProfileUrl,
        fundingSources,
      } = context;
      // re-request employee count if it's an old enum type (pre-OBS4)
      const isOldEECount = isOldEECountEnumType(employeeCount);
      const isOldSales = isOldSalesVolume(monthlySales);
      const isOldCompany = isOldCompanyType(isEcomm);
      const eeCount = !isOldEECount ? employeeCount : null;
      const salesVolume = isOldSales ? null : monthlySales;
      const companyType = isOldCompany ? null : isEcomm;
      if (isInlineEmail) {
        return {
          singlePageVbMmV0: {
            email: workEmail,
            selfAttestedRefCode: refCode,
          },
        };
      }

      return {
        multiPageVbMmV0: {
          firstName,
          lastName,
          employeeCount: null,
          companyName,
          linkedinProfileUrl,
          salesVolumeV2: null,
          email: workEmail,
          leadSelfAttestedType: null,
          selfAttestedRefCode: refCode,
          phoneNumber:
            country && mobileNumber
              ? formatInternationalPhoneNumber({
                  country,
                  value: mobileNumber,
                })
              : undefined,
          salesRepName,
          website,
          fundingSources: null,
          segmentationDataV3: {
            companyType: companyType as CompanyTypeV3,
            employeeCount: eeCount as EmployeeCountV3,
            fundingSources: (fundingSources as FundingSourceV3[]) ?? [],
            salesVolume: salesVolume as SalesVolumeV3,
          },
        },
      };
    },
    [isInlineEmail],
  );

  const createLeadMutation = React.useCallback(
    async (context: leadFormMachineContext, event) => {
      try {
        const refCode = getApplicableRefCodes(pathname);
        const input: CreateLeadInput = {
          marketoProgramName: programName,
          clientMutationId: uuidv4(),
          idempotencyKey: uuidv4(),
          attributionMetadata: {
            fbc: getFBC(),
            fbp: getFBP(),
            gclid: getGCLID(),
            liFatId: getLiFatId(),
            fullUrl: window.location.href,
            referralCode: refCode ?? null,
            segmentAnonymousId: getAnonymousId(),
            edition: getEdition() as EmpowerTier,
            ...hiddenFields,
          },
          leadFormType: isInlineEmail
            ? LeadFormType.SINGLE_PAGE_VB_MM_V0
            : LeadFormType.MULTI_PAGE_VB_MM_V0,
          leadFormStatus:
            isTechEventUrl(pathname) ||
            event.value.prevStep === LeadFormSteps.gtmExistingLead
              ? LeadFormStatus.COMPLETED
              : LeadFormStatus.PENDING,
          leadDetails: getLeadDetails(event.value),
        };
        const { id, leadToken, salesSegment } = await createLead(input);
        const leadObj = await getLead(leadToken);
        const leadStatus = leadObj?.leadFormStatus;

        updateCookie("leadToken", leadToken);
        updateCookie("leadId", id);
        const cookieCategories = getCookieCategories();

        /**
         * identify with all of user info
         */
        identify({
          properties: {
            email: context.workEmail,
            formFillUrl: window.location.href,
            partnerId: getRefCode() ?? getUserRefCode() ?? null,
            formCta: "Submit",
            referrer: prevPath || document.referrer,
            salesSegment,
          },
          // "Marketo V2": false if cookieCategories is undefined
          integrations: { "Marketo V2": cookieCategories.performance },
        });

        trackEvent(
          new LeadCreatedEvent(LeadFormStatus.PENDING, {
            companyName: context.companyName,
            website: context.website,
            monthlySales: context.monthlySales,
            employeeCount: context.employeeCount,
            fundingSources: context.fundingSources,
            salesSegment,
          }),
        );

        return {
          id,
          leadToken,
          salesSegment,
          leadStatus,
          refCode,
        };
      } catch (error) {
        return Promise.reject({
          title: "Something went wrong",
          message: (
            <>
              Please refresh your page to try again. If the problem persists,
              contact <a href="mailto:support@brex.com">support@brex.com</a>.
            </>
          ),
        });
      }
    },
    [
      createLead,
      getLead,
      getLeadDetails,
      hiddenFields,
      identify,
      isInlineEmail,
      pathname,
      prevPath,
      programName,
      trackEvent,
    ],
  );

  const updateLeadMutation = React.useCallback(
    async (context: leadFormMachineContext): Promise<UpdateLeadResponse> => {
      try {
        const { id, status } = context;
        const input: UpdateLeadInput = {
          marketoProgramName: programName,
          clientMutationId: uuidv4(),
          idempotencyKey: uuidv4(),
          id,
          leadFormType: isInlineEmail
            ? LeadFormType.SINGLE_PAGE_VB_MM_V0
            : LeadFormType.MULTI_PAGE_VB_MM_V0,
          leadDetails: getLeadDetails(context),
          leadFormStatus: status ?? LeadFormStatus.PENDING,
        };

        const { data } = await updateLead(input);

        trackEvent(
          new LeadUpdatedEvent(status, {
            companyName: context.companyName,
            website: context.website,
            monthlySales: context.monthlySales,
            employeeCount: context.employeeCount,
            fundingSources: context.fundingSources,
            salesSegment: data.updateLead.lead.salesSegment,
          }),
        );
        const cookieCategories = getCookieCategories();

        /**
         * identify with all of user info
         */
        identify({
          properties: {
            email: context.workEmail,
            formFillUrl: window.location.href,
            salesSegment: data.updateLead.lead.salesSegment,
          },
          // "Marketo V2": false if cookie groups is undefined
          integrations: { "Marketo V2": cookieCategories.performance },
        });

        return {
          id: data.updateLead.lead.id,
          leadToken: data.updateLead.lead.leadToken,
          salesSegment: data.updateLead.lead.salesSegment,
        };
      } catch (e) {
        console.log(e);
        return Promise.reject({
          title: "Something went wrong",
          message: (
            <>
              Please refresh your page to try again. If the problem persists,
              contact <a href="mailto:support@brex.com">support@brex.com</a>.
            </>
          ),
        });
      }
    },
    [
      getLeadDetails,
      identify,
      isInlineEmail,
      programName,
      trackEvent,
      updateLead,
    ],
  );

  const softSignupMutation = React.useCallback(
    async (context: leadFormMachineContext) => {
      await submitUserDataWithFormValue(context);
    },
    [submitUserDataWithFormValue],
  );

  const softSignupV2Mutation = React.useCallback(
    async (context: leadFormMachineContext) => {
      await submitUserData(context);
    },
    [submitUserData],
  );

  /**
   * Logic to determine which machine to use,
   * with the work on supporting lead forms for GTM, we add another logic to
   * check for what formType.
   */

  const getCorrectMachine = React.useCallback(() => {
    const isGTMLeadForm = formType === FormType.GTM_LEAD_FORM;
    const isTechEvent = isTechEventUrl(pathname);
    if (isTechEvent) {
      return externalEventMachine;
    }
    if (isInlineEmail) {
      return gtmEmailOnlyMachine;
    }
    if (isGTMLeadForm) {
      return gtmMachine;
    }

    return obs4Machine;
  }, [formType, pathname, isInlineEmail]);

  const machine = getCorrectMachine();
  const [state, send] = useMachine(machine, {
    services: {
      createLeadMutation,
      updateLeadMutation,
      softSignupMutation,
      softSignupV2Mutation,
    },
    context: {
      leadToken: Cookies.get("leadToken"),
      id: Cookies.get("leadId"),
      refCode: getApplicableRefCodes(pathname) ?? undefined,
    },
  });

  return { state, send };
};
